import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ToastOptions {
  action?: { label: string; onAction(): void | Promise<void> };
  durationMs?: number;
  tone?: 'neutral' | 'error';
}

interface Toast extends ToastOptions {
  id: number;
  message: string;
}

const ToastContext = createContext<(message: string, options?: ToastOptions) => void>(() => undefined);

/**
 * Brief confirmations ("Deleted · Undo"). Rendered in a polite live region so
 * screen readers announce them without moving focus.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = next.current++;
      setToasts((t) => [...t.slice(-2), { id, message, ...options }]);
      setTimeout(() => dismiss(id), options.durationMs ?? (options.action ? 7000 : 4000));
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="ui-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`ui-toast${t.tone === 'error' ? ' ui-toast--error' : ''}`}>
            <span className="ui-toast__msg">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  dismiss(t.id);
                  void t.action!.onAction();
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

/** Convenience: show a delete confirmation with Undo. */
export function useUndoToast() {
  const toast = useToast();
  return useMemo(
    () => (message: string, undo: { undo(): Promise<void> }) => toast(message, { action: { label: 'Undo', onAction: () => undo.undo() } }),
    [toast],
  );
}
